import { useState } from 'react';
import { ProjectType } from '../types/timesheet.types';
import type { ISODateString, PeriodMonth } from '../types/timesheet.types';
import type { SuperApprovalItem } from '../types/approval.types';
import { useUnlockGroupTimesheets } from '../hooks/useUnlockGroupTimesheets';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  return name.split(' ').map(x => x[0]).join('').slice(0, 2).toUpperCase();
}

function formatPeriodMonthLabel(pm: PeriodMonth): string {
  const [year, month] = pm.split('-');
  const months = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December',
  ];
  return `${months[parseInt(month, 10) - 1]} ${year}`;
}

function formatDate(iso: ISODateString): string {
  const d = new Date(iso + 'T00:00:00Z');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${d.getUTCDate()} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

// ─── Icons ───────────────────────────────────────────────────────────────────

function UnlockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 12, height: 12, strokeWidth: 2.5 }}>
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 9.9-1" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 13, height: 13, strokeWidth: 2 }}>
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  );
}

// ─── Unlock Card ─────────────────────────────────────────────────────────────

function UnlockCard({
  item,
  isActioning,
  onUnlock,
}: {
  item: SuperApprovalItem;
  isActioning: boolean;
  onUnlock: () => void;
}) {
  const monthLabel = formatPeriodMonthLabel(item.periodMonth);
  const rdHours    = item.breakdownByType[ProjectType.RD]    ?? 0;
  const gaHours    = item.breakdownByType[ProjectType.GA]    ?? 0;
  const leaveHours = item.breakdownByType[ProjectType.Leave] ?? 0;

  return (
    <div className="ts-card" style={{ marginBottom: 14 }}>
      <div className="ts-card-hdr">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div className="av" style={{ width: 28, height: 28, fontSize: 11, flexShrink: 0 }}>
            {getInitials(item.personName)}
          </div>
          <div>
            <div className="ts-card-title">
              {item.personName}
              {item.source === 'asset' && item.assetCode && (
                <span className="badge" style={{
                  marginLeft: 8,
                  background: 'rgba(99,102,241,0.15)',
                  color: 'var(--accent)',
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                }}>
                  {item.assetCode}
                </span>
              )}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 1 }}>
              {item.department && <span>{item.department} · </span>}
              {monthLabel}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
              {item.totalHours}h
            </div>
            <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 1 }}>
              {rdHours > 0 && <span>R&D {rdHours}h </span>}
              {gaHours > 0 && <span>G&A {gaHours}h </span>}
              {leaveHours > 0 && <span>Leave {leaveHours}h</span>}
            </div>
          </div>
          <button
            className="btn btn-ghost btn-sm"
            onClick={onUnlock}
            disabled={isActioning}
            title="Unlock this timesheet"
          >
            <UnlockIcon />&nbsp;Unlock
          </button>
        </div>
      </div>

      {item.superApprovedDate && (
        <div style={{
          padding: '6px 18px 8px',
          fontSize: 11.5,
          color: 'var(--text-muted)',
          borderTop: '1px solid var(--border)',
        }}>
          Final approved {formatDate(item.superApprovedDate)}
          {item.superApproverName && <span> by {item.superApproverName}</span>}
        </div>
      )}
    </div>
  );
}

// ─── Unlock Modal ─────────────────────────────────────────────────────────────

function UnlockModal({
  item,
  note,
  error,
  isActioning,
  onNoteChange,
  onConfirm,
  onClose,
}: {
  item: SuperApprovalItem;
  note: string;
  error: string;
  isActioning: boolean;
  onNoteChange: (v: string) => void;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const [step, setStep] = useState<1 | 2>(1);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(217,119,6,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <UnlockIcon />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.01em' }}>
              Unlock Timesheet
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 1 }}>
              {step === 1 ? 'This will reset the timesheet to Draft for editing' : 'Step 2 of 2 — Confirm unlock'}
            </div>
          </div>
        </div>

        <div style={{ padding: '14px 20px 8px' }}>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 10 }}>
            <strong style={{ color: 'var(--text)' }}>{item.personName}</strong>
            {' — '}{formatPeriodMonthLabel(item.periodMonth)}
          </div>
        </div>

        {step === 1 ? (
          <>
            <div style={{ padding: '0 20px 16px' }}>
              <label style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: 6 }}>
                Reason for unlock&nbsp;<span style={{ color: 'var(--red)', fontWeight: 700 }}>*</span>
              </label>
              <textarea
                className="fnote"
                value={note}
                onChange={e => onNoteChange(e.target.value)}
                placeholder="Explain why this timesheet is being unlocked…"
                style={{ resize: 'vertical', minHeight: 90 }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, padding: '12px 20px', borderTop: '1px solid var(--border)', justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={isActioning}>Cancel</button>
              <button
                className="btn btn-sm"
                style={{ minWidth: 100, background: '#d97706', color: '#fff', border: 'none' }}
                onClick={() => { if (note.trim()) setStep(2); }}
                disabled={!note.trim() || isActioning}
              >
                Next →
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ padding: '0 20px 16px' }}>
              <div style={{
                background: 'rgba(254,243,199,0.6)',
                border: '1px solid rgba(217,119,6,0.25)',
                borderRadius: 10,
                padding: '14px 16px',
              }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#92400e', marginBottom: 6 }}>
                  Are you sure you want to unlock this timesheet?
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
                  This will remove all approval dates and return the timesheet to <strong>Draft</strong>,
                  requiring the employee to re-submit for approval.
                </div>
                <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  Reason: {note}
                </div>
              </div>
              {error && <div className="err-banner" style={{ marginTop: 10 }}>{error}</div>}
            </div>
            <div style={{ display: 'flex', gap: 8, padding: '12px 20px', borderTop: '1px solid var(--border)', justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setStep(1)} disabled={isActioning}>← Back</button>
              <button
                className="btn btn-sm"
                style={{ minWidth: 130, background: '#d97706', color: '#fff', border: 'none' }}
                onClick={onConfirm}
                disabled={isActioning}
              >
                {isActioning
                  ? <><span className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />&nbsp;Unlocking…</>
                  : <><UnlockIcon />&nbsp;Yes, Unlock</>
                }
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── UnlockGroupDashboard ────────────────────────────────────────────────────

export interface UnlockGroupDashboardProps {
  unlockedByName: string;
  unlockedByEmail: string;
}

export function UnlockGroupDashboard({ unlockedByName, unlockedByEmail }: UnlockGroupDashboardProps) {
  const [unlockTarget, setUnlockTarget] = useState<SuperApprovalItem | null>(null);
  const [unlockNote, setUnlockNote] = useState('');
  const [unlockError, setUnlockError] = useState('');

  const { items, isLoading, loadError, isActioning, actionError, unlockMonth, refresh } =
    useUnlockGroupTimesheets();

  const openUnlock = (item: SuperApprovalItem) => {
    setUnlockTarget(item);
    setUnlockNote('');
    setUnlockError('');
  };

  const handleUnlockConfirm = async () => {
    if (!unlockTarget) return;
    setUnlockError('');
    try {
      await unlockMonth(unlockTarget, unlockNote, unlockedByName, unlockedByEmail);
      setUnlockTarget(null);
      setUnlockNote('');
    } catch (e) {
      setUnlockError(e instanceof Error ? e.message : 'Unlock failed.');
    }
  };

  const employeeItems = items.filter(a => a.source === 'employee');
  const assetItems    = items.filter(a => a.source === 'asset');

  return (
    <div id="view-unlock" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {/* Topbar */}
      <div className="topbar">
        <div className="topbar-l">
          <div className="crumb">
            <span>Timesheets</span>
            <span className="sep">/</span>
            <span className="cur">Unlock Timesheets</span>
          </div>
        </div>
        <div className="topbar-r">
          {items.length > 0 && (
            <span style={{ fontSize: 12, color: 'var(--text-muted)', marginRight: 12 }}>
              {items.length} locked {items.length === 1 ? 'timesheet' : 'timesheets'}
            </span>
          )}
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => void refresh()}
            disabled={isLoading}
            title="Refresh"
          >
            <RefreshIcon />&nbsp;Refresh
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', background: 'var(--bg)', padding: '20px 28px' }}>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
            Final Approved Timesheets
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            All locked timesheets — use Unlock to return a timesheet to Draft for correction
          </div>
        </div>

        {(loadError || actionError) && (
          <div className="err-banner" style={{ marginBottom: 16 }}>
            {(loadError ?? actionError)!.message}
          </div>
        )}

        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 48 }}>
            <div className="spinner" aria-label="Loading" />
          </div>
        ) : items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-dim)', fontSize: 13 }}>
            No final-approved timesheets found
          </div>
        ) : (
          <>
            {employeeItems.length > 0 && (
              <>
                {assetItems.length > 0 && (
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', margin: '4px 2px 10px', letterSpacing: '0.02em' }}>
                    Employee Timesheets <span style={{ fontSize: 11.5, color: 'var(--text-muted)', fontWeight: 400 }}>{employeeItems.length} items</span>
                  </div>
                )}
                {employeeItems.map(item => (
                  <UnlockCard
                    key={`emp::${item.personId}::${item.periodMonth}`}
                    item={item}
                    isActioning={isActioning}
                    onUnlock={() => openUnlock(item)}
                  />
                ))}
              </>
            )}
            {assetItems.length > 0 && (
              <>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', margin: `${employeeItems.length > 0 ? 12 : 4}px 2px 10px`, letterSpacing: '0.02em' }}>
                  Asset Timesheets <span style={{ fontSize: 11.5, color: 'var(--text-muted)', fontWeight: 400 }}>{assetItems.length} items</span>
                </div>
                {assetItems.map(item => (
                  <UnlockCard
                    key={`ast::${item.personId}::${item.periodMonth}::${item.assetCode}`}
                    item={item}
                    isActioning={isActioning}
                    onUnlock={() => openUnlock(item)}
                  />
                ))}
              </>
            )}
          </>
        )}
      </div>

      {unlockTarget && (
        <UnlockModal
          item={unlockTarget}
          note={unlockNote}
          error={unlockError}
          isActioning={isActioning}
          onNoteChange={setUnlockNote}
          onConfirm={() => void handleUnlockConfirm()}
          onClose={() => setUnlockTarget(null)}
        />
      )}
    </div>
  );
}
